import { resolveLocale, withLangParam } from '@/components/Functional/utils/publicPageLocale'
import { useRouter } from 'next/router'

/**
 * 与えられた文字列が有効なキャンペーンスラグかどうかを判定する純粋関数。
 *
 * Next.js の静的エクスポート環境では hydration 前に `router.asPath` や
 * `router.query.campaign` がプレースホルダー文字列 `'[campaign]'` / `'/[campaign]'`
 * を返すことがあるため、それらを無効スラグとして除外する。
 */
export function isValidCampaignSlug(slug: string | null | undefined): slug is string {
  return !!slug && slug !== '[campaign]' && slug !== '/[campaign]'
}

interface UseCampaignSlugProps {
  campaignSlug: string
  /**
   * ログイン時に利用するURL
   * */
  loginUrl: string
  /**
   * useRouterなどで取得したpathを元に、campaignSlugを付与したURLを生成する
   * */
  gen: (path: `/${string}`) => string
  /**
   * 現在のパスがホームかどうか。
   * /{campaignSlug}/ のパスであればtrue、それ以外はfalse
   * */
  isHome: boolean
  LOCAL_STORAGE_DEPLOY_TIMESTAMP_KEY: string
  LOCAL_STORAGE_I18N_KEY: string
  LOCAL_STORAGE_USERNAME_KEY: string
  LOCAL_STORAGE_PERIOD_KEY: string
  LOCAL_STORAGE_EXPIRATION_TIME_KEY: string
  LOCAL_STORAGE_CLIENT_ID_KEY: string
  LOCAL_STORAGE_REGISTER_QUESTIONNAIRES_KEY: string
  LOCAL_STORAGE_QRCODE_KEY: string
  LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY: string
  LOCAL_STORAGE_SPOT_SELECTION_KEY: string
  LOCAL_STORAGE_OCR_CONFIRM_KEY: string
  LOCAL_STORAGE_REWARD_LIST_SELECTED_KEY: string
  LOCAL_STORAGE_POPUP_HOOKPOINT_KEY: (hookPoint: string) => string
  LOCAL_STORAGE_USER_DEVICE_UUID_KEY: string
}

/**
 * slugからcampaignを取得し、そのほかのlocalStorageなどのキーも取得する
 *  */
export const useCampaignSlug = (): UseCampaignSlugProps => {
  const router = useRouter()
  const { campaign } = router.query
  const path = router.asPath
  const altCampaign = (path.split('/')[1] ?? '').split(/[?#]/)[0]
  // /{campaignSlug}/ のパスであればhome以下とみなしtrue、それ以外はfalse
  const isHome = path.split('/').length - 1 <= 2
  const withoutBasePath = process.env.NEXT_PUBLIC_WITHOUT_BASE_PATH as '0' | '1'
  const serviceName = process.env.NEXT_PUBLIC_SERVICE_NAME ?? process.env.NEXT_PUBLIC_CAMPAIGN_SLUG ?? ''

  /** 現在のページURL を return_to、表示中のロケールを lang として付与した Auth サーバへの loginUrl を生成する */
  const buildLoginUrl = (slug: string): string => {
    // SSR時は NEXT_PUBLIC_SITEURL、CSR時は実際のオリジンを使用（独自ドメイン対応）
    const isClient = typeof window !== 'undefined'
    const siteUrl = isClient ? window.location.origin : (process.env.NEXT_PUBLIC_SITEURL ?? '')
    // hydration 前は router.asPath が '[campaign]' 等のプレースホルダーになるため return_to を付与しない
    const isPlaceholder = path.includes('[')
    const returnTo = siteUrl && !isPlaceholder ? `?return_to=${encodeURIComponent(siteUrl + path)}` : ''
    // Auth 画面のロケールは `lang` クエリで渡す（#3504）。
    // ロケールは `{slug}_i18nextLng`（= LOCAL_STORAGE_I18N_KEY）から引くため、
    // 各分岐で組み立てているキーと同じ規則にすること。
    return withLangParam(
      `${process.env.NEXT_PUBLIC_AUTH_V2_URL}/${slug}${returnTo}`,
      resolveLocale(`${slug}_i18nextLng`),
    )
  }

  if (withoutBasePath === '0') {
    const cleanedCampaign = typeof campaign === 'string' ? campaign.split(/[?#]/)[0] : campaign
    if (typeof cleanedCampaign === 'string' && isValidCampaignSlug(cleanedCampaign)) {
      return {
        campaignSlug: cleanedCampaign,
        loginUrl: buildLoginUrl(cleanedCampaign),
        gen: (path: `/${string}`) => `/${cleanedCampaign}${path}`,
        isHome,
        LOCAL_STORAGE_DEPLOY_TIMESTAMP_KEY: `${cleanedCampaign}_DEPLOY_TIMESTAMP`,
        LOCAL_STORAGE_I18N_KEY: `${cleanedCampaign}_i18nextLng`,
        LOCAL_STORAGE_USERNAME_KEY: `${cleanedCampaign}-username`,
        LOCAL_STORAGE_PERIOD_KEY: `${cleanedCampaign}-period`,
        LOCAL_STORAGE_EXPIRATION_TIME_KEY: `${cleanedCampaign}-expiration-time`,
        LOCAL_STORAGE_CLIENT_ID_KEY: `${cleanedCampaign}-clientid`,
        LOCAL_STORAGE_REGISTER_QUESTIONNAIRES_KEY: `${cleanedCampaign}-register-Questionnaires`,
        LOCAL_STORAGE_QRCODE_KEY: `${cleanedCampaign}-qrcode`,
        LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY: `${cleanedCampaign}-unauthorized-count`,
        LOCAL_STORAGE_SPOT_SELECTION_KEY: `${cleanedCampaign}-spot-selection`,
        LOCAL_STORAGE_OCR_CONFIRM_KEY: `${cleanedCampaign}-confirm-key`,
        LOCAL_STORAGE_REWARD_LIST_SELECTED_KEY: `${cleanedCampaign}-reward-list-selected-key`,
        LOCAL_STORAGE_POPUP_HOOKPOINT_KEY: (hookPoint: string) => `${cleanedCampaign}-${hookPoint}-popup`,
        LOCAL_STORAGE_USER_DEVICE_UUID_KEY: `${cleanedCampaign}-user-device-uuid`,
      }
    } else if (path && isValidCampaignSlug(altCampaign)) {
      return {
        campaignSlug: altCampaign,
        loginUrl: buildLoginUrl(altCampaign),
        gen: (path: `/${string}`) => `/${altCampaign}${path}`,
        isHome,
        LOCAL_STORAGE_DEPLOY_TIMESTAMP_KEY: `${altCampaign}_DEPLOY_TIMESTAMP`,
        LOCAL_STORAGE_I18N_KEY: `${altCampaign}_i18nextLng`,
        LOCAL_STORAGE_USERNAME_KEY: `${altCampaign}-username`,
        LOCAL_STORAGE_PERIOD_KEY: `${altCampaign}-period`,
        LOCAL_STORAGE_EXPIRATION_TIME_KEY: `${altCampaign}-expiration-time`,
        LOCAL_STORAGE_CLIENT_ID_KEY: `${altCampaign}-clientid`,
        LOCAL_STORAGE_REGISTER_QUESTIONNAIRES_KEY: `${altCampaign}-register-Questionnaires`,
        LOCAL_STORAGE_QRCODE_KEY: `${altCampaign}-qrcode`,
        LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY: `${altCampaign}-unauthorized-count`,
        LOCAL_STORAGE_SPOT_SELECTION_KEY: `${altCampaign}-spot-selection`,
        LOCAL_STORAGE_OCR_CONFIRM_KEY: `${altCampaign}-confirm-key`,
        LOCAL_STORAGE_REWARD_LIST_SELECTED_KEY: `${altCampaign}-reward-list-selected-key`,
        LOCAL_STORAGE_POPUP_HOOKPOINT_KEY: (hookPoint: string) => `${altCampaign}-${hookPoint}-popup`,
        LOCAL_STORAGE_USER_DEVICE_UUID_KEY: `${altCampaign}-user-device-uuid`,
      }
    }
  } else if (withoutBasePath === '1') {
    // 現在、next.jsの`pages/[campaign]`のルーティングを採用している都合上、`/`で終わることができない
    const campaignFromEnv = process.env.NEXT_PUBLIC_CAMPAIGN_SLUG
    if (campaignFromEnv) {
      return {
        campaignSlug: campaignFromEnv,
        loginUrl: buildLoginUrl(campaignFromEnv),
        gen: (path: `/${string}`) => path,
        isHome: path.split('/').length - 1 <= 1,
        LOCAL_STORAGE_DEPLOY_TIMESTAMP_KEY: `${campaignFromEnv}_DEPLOY_TIMESTAMP`,
        LOCAL_STORAGE_I18N_KEY: `${campaignFromEnv}_i18nextLng`,
        LOCAL_STORAGE_USERNAME_KEY: `${campaignFromEnv}-username`,
        LOCAL_STORAGE_PERIOD_KEY: `${campaignFromEnv}-period`,
        LOCAL_STORAGE_EXPIRATION_TIME_KEY: `${campaignFromEnv}-expiration-time`,
        LOCAL_STORAGE_CLIENT_ID_KEY: `${campaignFromEnv}-clientid`,
        LOCAL_STORAGE_REGISTER_QUESTIONNAIRES_KEY: `${campaignFromEnv}-register-Questionnaires`,
        LOCAL_STORAGE_QRCODE_KEY: `${campaignFromEnv}-qrcode`,
        LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY: `${campaignFromEnv}-unauthorized-count`,
        LOCAL_STORAGE_SPOT_SELECTION_KEY: `${campaignFromEnv}-spot-selection`,
        LOCAL_STORAGE_OCR_CONFIRM_KEY: `${campaignFromEnv}-confirm-key`,
        LOCAL_STORAGE_REWARD_LIST_SELECTED_KEY: `${campaignFromEnv}-reward-list-selected-key`,
        LOCAL_STORAGE_POPUP_HOOKPOINT_KEY: (hookPoint: string) => `${campaignFromEnv}-${hookPoint}-popup`,
        LOCAL_STORAGE_USER_DEVICE_UUID_KEY: `${campaignFromEnv}-user-device-uuid`,
      }
    }
  }

  return {
    campaignSlug: serviceName,
    loginUrl: buildLoginUrl(serviceName),
    gen: (path: `/${string}`) => `/${serviceName}${path}`,
    isHome,
    LOCAL_STORAGE_DEPLOY_TIMESTAMP_KEY: `${serviceName}_DEPLOY_TIMESTAMP`,
    LOCAL_STORAGE_I18N_KEY: `${serviceName}_i18nextLng`,
    LOCAL_STORAGE_USERNAME_KEY: `${serviceName}-username`,
    LOCAL_STORAGE_PERIOD_KEY: `${serviceName}-period`,
    LOCAL_STORAGE_EXPIRATION_TIME_KEY: `${serviceName}-expiration-time`,
    LOCAL_STORAGE_CLIENT_ID_KEY: `${serviceName}-clientid`,
    LOCAL_STORAGE_REGISTER_QUESTIONNAIRES_KEY: `${serviceName}-register-Questionnaires`,
    LOCAL_STORAGE_QRCODE_KEY: `${serviceName}-qrcode`,
    LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY: `${serviceName}-unauthorized-count`,
    LOCAL_STORAGE_SPOT_SELECTION_KEY: `${serviceName}-spot-selection`,
    LOCAL_STORAGE_OCR_CONFIRM_KEY: `${serviceName}-confirm-key`,
    LOCAL_STORAGE_REWARD_LIST_SELECTED_KEY: `${serviceName}-reward-list-selected-key`,
    LOCAL_STORAGE_POPUP_HOOKPOINT_KEY: (hookPoint: string) => `${serviceName}-${hookPoint}-popup`,
    LOCAL_STORAGE_USER_DEVICE_UUID_KEY: `${serviceName}-user-device-uuid`,
  }
}
